import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, MapPin, RefreshCw, Trash2, XCircle,
} from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Gym Submissions Tab ───
export default function GymSubmissionsTab() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState({ submissions: [], counts: {} });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API_URL}/api/admin/gym-submissions${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(await res.json());
    } catch (err) { console.error('Fetch gym submissions failed:', err); }
    finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleAction = async (id, action, reason = '') => {
    const opts = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    };
    if (action === 'reject' && reason) opts.body = JSON.stringify({ reason });
    await fetch(`${API_URL}/api/admin/gym-submissions/${id}/${action}`, opts);
    fetchSubs();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    await fetch(`${API_URL}/api/admin/gym-submissions/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    fetchSubs();
  };

  return (
    <div className="space-y-6" data-testid="gym-submissions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gym Directory Submissions</h2>
          <p className="text-sm text-gray-500">Approve or reject gym listing requests</p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`gym-filter-${f}`}>
              {f} {data.counts[f] !== undefined ? `(${data.counts[f]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Pending', val: data.counts.pending || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Approved', val: data.counts.approved || 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Rejected', val: data.counts.rejected || 0, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border border-gray-100`}>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto" /></div>
      ) : data.submissions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl"><p className="text-gray-400">No submissions for this filter</p></div>
      ) : (
        <div className="space-y-3">
          {data.submissions.map(sub => (
            <div key={sub.id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`gym-sub-${sub.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900">{sub.gym_name}</h3>
                    {sub.is_hyrox_affiliated && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-green-50 text-green-600 border border-green-200">HYROX</span>
                    )}
                    <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border rounded ${
                      sub.status === 'approved' ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                      : sub.status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200'
                      : 'bg-amber-50 text-amber-600 border-amber-200'
                    }`}>{sub.status}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{sub.address ? `${sub.address}, ` : ''}{sub.city}, {sub.state}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                    <span>{sub.contact_name} &mdash; {sub.contact_email}</span>
                    {sub.phone && <span>{sub.phone}</span>}
                    {sub.website && <a href={sub.website} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">{sub.website}</a>}
                  </div>
                  {sub.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{sub.description}</p>}
                  {sub.class_types?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">{sub.class_types.map(c => <span key={c} className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">{c}</span>)}</div>
                  )}
                </div>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  {sub.status === 'pending' && (
                    <>
                      <button onClick={() => handleAction(sub.id, 'approve')}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                        data-testid={`approve-${sub.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={() => {
                        const reason = window.prompt('Rejection reason (optional):');
                        if (reason !== null) handleAction(sub.id, 'reject', reason);
                      }}
                        className="px-3 py-1.5 bg-red-100 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-200 flex items-center gap-1"
                        data-testid={`reject-${sub.id}`}>
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDelete(sub.id)}
                    className="px-2 py-1.5 bg-gray-100 text-gray-400 text-xs rounded-lg hover:bg-gray-200 hover:text-gray-600"
                    data-testid={`delete-sub-${sub.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
